/**
 * Assessment Profiles — Scenario-aware SDK configuration
 *
 * Maps pronunciation assessment scenarios to optimal Azure SDK parameters.
 * Profile selection is based on reference text length (word count).
 * SDK parameters are adapted per-language using pronunciationFeatures.
 *
 * See: docs/specs/pronunciation-assessment-v2.spec.md (Phase 2)
 */

import type { AssessmentConfig } from './NativeAzureSpeech';

/**
 * Assessment profile — determines SDK scoring parameters.
 *
 * All profiles run on the continuous recognizer; the profile tunes what Azure
 * scores (granularity, prosody, miscue, scripted vs unscripted), not the
 * recognition mode.
 *
 * - word: Single word
 * - phrase: 2-6 words
 * - sentence: 7+ words
 * - free: No reference text (unscripted)
 */
export type AssessmentProfile = 'word' | 'phrase' | 'sentence' | 'free';

/**
 * Per-language pronunciation feature flags.
 * Seeded in languages.json → speechProviders.azure.pronunciationFeatures.
 * Determines which SDK features to request and what feedback the agent can give.
 */
export interface PronunciationFeatures {
  /** Named IPA/SAPI phoneme symbols available (not just scores) */
  phonemeLabels: boolean;
  /** Syllable grouping available (en-US only) */
  syllables: boolean;
  /** NBest phoneme alternatives — "what was actually spoken" (en-US only) */
  nbestPhonemes: boolean;
  /** Prosody scoring + per-word break/intonation feedback (en-US only) */
  prosody: boolean;
}

/** Default features for languages without pronunciationFeatures data */
const DEFAULT_FEATURES: PronunciationFeatures = {
  phonemeLabels: false,
  syllables: false,
  nbestPhonemes: false,
  prosody: false,
};

/**
 * SDK config overrides per profile, adapted to language capabilities.
 * These are merged on top of ASSESSMENT_DEFAULTS.
 */
type ProfileConfigFn = (features: PronunciationFeatures) => Partial<AssessmentConfig>;

// `enableTwoPassUnscriptedAssessment` selects Azure's UNSCRIPTED assessment path,
// which uses a different STT model that recognizes freely and does NOT score
// against a reference text (Azure docs: "For unscripted assessment, the STT model
// used is different from Azure STT"). Leaving it on for a scripted profile makes
// Azure score the recognized word ("read" spoken → a clean "rate") instead of the
// TARGET. So it must be OFF for every scripted profile (word/phrase/sentence) and
// ON only for `free` (no reference), where it genuinely improves accuracy.
const PROFILE_CONFIGS: Record<AssessmentProfile, ProfileConfigFn> = {
  word: (f) => ({
    granularity: 'Phoneme',
    enableProsodyAssessment: f.prosody,
    nbestPhonemeCount: f.nbestPhonemes ? 3 : 0,
    enableMiscue: false, // Single word — miscue adds no value
    enableTwoPassUnscriptedAssessment: false, // scripted — score against the reference word
    phonemeAlphabet: 'IPA',
  }),
  phrase: (f) => ({
    granularity: 'Phoneme',
    enableProsodyAssessment: f.prosody,
    nbestPhonemeCount: f.nbestPhonemes ? 3 : 0,
    enableMiscue: false, // Miscue (omission/insertion) is unsupported in continuous mode — inert
    enableTwoPassUnscriptedAssessment: false, // scripted — score against the reference
    phonemeAlphabet: 'IPA',
  }),
  sentence: (f) => ({
    granularity: 'Phoneme', // Extract phonemes but only surface issues
    enableProsodyAssessment: f.prosody,
    nbestPhonemeCount: 0, // Too much data for long sentences
    enableMiscue: false, // Miscue (omission/insertion) is unsupported in continuous mode — inert
    enableTwoPassUnscriptedAssessment: false, // scripted — score against the reference
    phonemeAlphabet: 'IPA',
  }),
  free: () => ({
    granularity: 'Word', // No reference text — phonemes less useful
    enableProsodyAssessment: false,
    nbestPhonemeCount: 0,
    enableMiscue: false, // No reference text to compare against
    enableTwoPassUnscriptedAssessment: true, // unscripted — improves free-speech accuracy
    phonemeAlphabet: 'IPA',
  }),
};

/**
 * Select assessment profile from reference text.
 *
 * - 1 word → 'word'
 * - 2-6 words → 'phrase'
 * - 7+ words → 'sentence'
 * - No reference text → 'free'
 */
export function selectProfile(referenceText?: string): AssessmentProfile {
  if (!referenceText?.trim()) return 'free';
  const wordCount = referenceText.trim().split(/\s+/).length;
  if (wordCount <= 1) return 'word';
  if (wordCount <= 6) return 'phrase';
  return 'sentence';
}

/**
 * Get SDK config overrides for a profile + language combination.
 * Merge result on top of ASSESSMENT_DEFAULTS + session-specific fields.
 */
export function getProfileConfig(
  profile: AssessmentProfile,
  features?: PronunciationFeatures | null,
): Partial<AssessmentConfig> {
  return PROFILE_CONFIGS[profile](features ?? DEFAULT_FEATURES);
}
